<template>
  <div class="workflow-ping-instructions-modal">
    <CustomModal @onClose="$emit('close')">
      <div
        ref="modalContentWrapper"
        class="modal-content-wrapper"
        :style="modalWrapperStyle"
      >
        <div class="heading-row">
          <h3>Zum Homescreen hinzufügen</h3>
        </div>

        <p class="subtitle">
          Öffnen Sie den Workflow als App-Verknüpfung auf Ihrem Smartphone.
        </p>

        <div class="steps">
          <div
            v-for="(step, idx) in instructionSet.steps"
            :key="idx"
            class="step"
          >
            <div class="step-index">{{ idx + 1 }}</div>
            <div class="step-content">
              <h4>{{ step.title }}</h4>
              <p>{{ step.text }}</p>
            </div>
          </div>
        </div>

        <div v-if="isQuickPreviewAvailable" class="quick-preview-toggle">
          <button type="button" @click="toggleQuickPreview">
            <i class="fa fa-play-circle"></i>
            <span>
              {{
                isQuickPreviewVisible
                  ? 'Schnellvorschau ausblenden'
                  : 'Schnellvorschau anzeigen (alle Schritte)'
              }}
            </span>
          </button>
        </div>

        <div v-if="isQuickPreviewVisible" class="quick-preview-media">
          <img
            :key="`${resolvedPreviewSrc}-${previewReplayKey}`"
            :src="resolvedPreviewSrc"
            alt="Schnellvorschau: Workflow zum Homescreen hinzufügen"
            @error="onPreviewMediaError"
            @click="replayPreview"
          />
          <p class="quick-preview-caption">
            Diese Vorschau zeigt den kompletten Ablauf zum Hinzufügen auf den
            Homescreen. Tippen/Klicken Sie auf das GIF, um es erneut
            abzuspielen.
          </p>
        </div>
        <div class="tip">
          <i class="fa fa-check"></i>
          <span>{{ instructionSet.tip }}</span>
        </div>

        <div class="actions">
          <button type="button" class="open-btn" @click="openWorkflow">
            Im Browser öffnen
          </button>
        </div>
      </div>
    </CustomModal>
  </div>
</template>

<script>
import CustomModal from './CustomModal.vue';
import homePreviewFallback from '@/assets/img/home-1.png';
import iosHomeInstallGif from '@/assets/img/codelos_clean_highlight.gif';
import androidHomeInstallGif from '@/assets/img/codelos_android_install.gif';

export default {
  name: 'WorkflowPingInstructionsModal',
  props: {
    workflowLink: {
      type: String,
      default: '',
    },
    previewMediaSrc: {
      type: String,
      default: '',
    },
    androidPreviewMediaSrc: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      platform: 'other',
      isQuickPreviewVisible: false,
      resolvedPreviewSrc: '',
      previewReplayKey: 0,
      lockedModalHeight: '',
    };
  },
  computed: {
    modalWrapperStyle() {
      return this.lockedModalHeight ? { height: this.lockedModalHeight } : {};
    },
    isQuickPreviewAvailable() {
      return true;
    },
    instructionSet() {
      const map = {
        ios: {
          steps: [
            {
              title: 'Workflow in Safari öffnen',
              text: 'Öffnen Sie den Workflow-Link in Safari auf Ihrem iPhone.',
            },
            {
              title: 'Teilen-Button tippen',
              text: 'Tippen Sie unten oder oben auf das Teilen-Symbol.',
            },
            {
              title: 'Zum Home-Bildschirm',
              text: 'Wählen Sie "Zum Home-Bildschirm" und bestätigen Sie mit "Hinzufügen".',
            },
          ],
          tip: 'Danach startet der Workflow direkt wie eine App vom Homescreen.',
        },
        android: {
          steps: [
            {
              title: 'Workflow in Chrome öffnen',
              text: 'Öffnen Sie den Workflow-Link in Chrome.',
            },
            {
              title: 'Menü öffnen',
              text: 'Tippen Sie oben rechts auf das Menü (drei Punkte).',
            },
            {
              title: 'Zum Startbildschirm hinzufügen',
              text: 'Wählen Sie "Zum Startbildschirm hinzufügen" und bestätigen Sie.',
            },
          ],
          tip: 'So können Sie den Workflow später mit einem Tap starten.',
        },
        other: {
          steps: [
            {
              title: 'Workflow-Link öffnen',
              text: 'Öffnen Sie den Workflow in Safari (iPhone) oder Chrome (Android).',
            },
            {
              title: 'Browser-Menü öffnen',
              text: 'Nutzen Sie in Safari "Teilen" oder in Chrome das Menü.',
            },
            {
              title: 'Homescreen-Option wählen',
              text: 'Wählen Sie "Zum Home-Bildschirm" oder "Zum Startbildschirm hinzufügen".',
            },
          ],
          tip: 'Mit dem Homescreen-Shortcut starten Sie den Workflow schneller.',
        },
      };
      return map[this.platform] || map.other;
    },
  },
  mounted() {
    const ua = navigator.userAgent || navigator.vendor || window.opera || '';
    const isAndroid = /android/i.test(ua);
    const isIOS =
      /iPad|iPhone|iPod/.test(ua) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    if (isIOS) this.platform = 'ios';
    else if (isAndroid) this.platform = 'android';
    else this.platform = 'other';

    if (this.platform === 'android') {
      this.resolvedPreviewSrc =
        this.androidPreviewMediaSrc || androidHomeInstallGif;
    } else {
      this.resolvedPreviewSrc = this.previewMediaSrc || iosHomeInstallGif;
    }

    this.$nextTick(() => {
      this.lockModalHeight();
    });
    window.addEventListener('resize', this.onResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },
  methods: {
    toggleQuickPreview() {
      this.isQuickPreviewVisible = !this.isQuickPreviewVisible;
    },
    lockModalHeight() {
      const wrapper = this.$refs.modalContentWrapper;
      if (!wrapper) return;
      this.lockedModalHeight = `${wrapper.offsetHeight}px`;
    },
    onResize() {
      this.lockedModalHeight = '';
      this.$nextTick(() => {
        this.lockModalHeight();
      });
    },
    replayPreview() {
      this.previewReplayKey += 1;
    },
    onPreviewMediaError() {
      this.resolvedPreviewSrc = homePreviewFallback;
    },
    openWorkflow() {
      if (!this.workflowLink) return;
      window.open(this.workflowLink, '_blank');
    },
  },
  components: {
    CustomModal,
  },
};
</script>

<style scoped>
.workflow-ping-instructions-modal ::v-deep .custom-modal-close-icon {
  top: 24px;
  right: 24px;
  width: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.workflow-ping-instructions-modal ::v-deep .custom-modal-close-icon svg {
  width: 20px;
  height: 20px;
}

.workflow-ping-instructions-modal .modal-content-wrapper {
  width: 760px;
  max-width: 100%;
  max-height: calc(100vh - 32px);
  overflow-y: scroll;
  overflow-x: hidden;
  scrollbar-gutter: stable both-edges;
  -webkit-overflow-scrolling: touch;
  margin: 52px 0 18px 0;
  padding: 0 40px;
  box-sizing: border-box;
}

.workflow-ping-instructions-modal .modal-content-wrapper {
  scrollbar-width: thin;
}

.workflow-ping-instructions-modal .modal-content-wrapper::-webkit-scrollbar {
  width: 6px;
}

.workflow-ping-instructions-modal
  .modal-content-wrapper::-webkit-scrollbar-track {
  background: transparent;
}

.workflow-ping-instructions-modal
  .modal-content-wrapper::-webkit-scrollbar-thumb {
  background: #8a8a8a;
  border-radius: 8px;
}

.workflow-ping-instructions-modal .heading-row {
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 12px;
}

.workflow-ping-instructions-modal h3 {
  margin: 0;
  font-size: 34px;
  line-height: 1.1;
  letter-spacing: -0.02em;
  color: #111111;
}

.workflow-ping-instructions-modal .subtitle {
  margin: 12px 0 22px;
  color: #5a5a66;
  font-size: 16px;
  line-height: 1.35;
}
.workflow-ping-instructions-modal .open-btn {
  min-height: 52px;
  border-radius: 12px;
  border: 1px solid #121212;
  background: #121212;
  color: #fff;
  padding: 0 18px;
  font-size: 14px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.workflow-ping-instructions-modal .steps {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  min-width: 0;
}

.workflow-ping-instructions-modal .quick-preview-toggle {
  margin-top: 14px;
}

.workflow-ping-instructions-modal .quick-preview-toggle button {
  width: 100%;
  min-height: 48px;
  border-radius: 12px;
  border: 1px solid #d7d7db;
  background: #fff;
  color: #111111;
  font-size: 13px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 12px;
}

.workflow-ping-instructions-modal .quick-preview-media {
  margin-top: 12px;
  border: 1px solid #dfdfe4;
  border-radius: 14px;
  overflow: hidden;
  background: #f8f8fa;
}

.workflow-ping-instructions-modal .quick-preview-media img {
  width: 100%;
  max-height: 360px;
  object-fit: contain;
  display: block;
  cursor: pointer;
}

.workflow-ping-instructions-modal .quick-preview-caption {
  margin: 0;
  padding: 10px 14px;
  font-size: 13px;
  color: #5a5a66;
  border-top: 1px solid #e6e6ea;
}

.workflow-ping-instructions-modal .quick-preview-note {
  margin: 12px 0 0;
  font-size: 13px;
  color: #5f5f67;
}

.workflow-ping-instructions-modal .step {
  border: 1px solid #dfdfe4;
  border-radius: 14px;
  padding: 14px;
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr);
  gap: 12px;
  background: #ffffff;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow: hidden;
}

.workflow-ping-instructions-modal .step-index {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f3f4f7;
  color: #111111;
  font-size: 15px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.workflow-ping-instructions-modal .step-content h4 {
  margin: 0 0 5px;
  font-size: 16px;
  color: #111111;
  word-break: break-word;
}

.workflow-ping-instructions-modal .step-content p {
  margin: 0;
  color: #5a5a66;
  font-size: 14px;
  line-height: 1.45;
  word-break: break-word;
}

.workflow-ping-instructions-modal .tip {
  margin-top: 16px;
  border: 1px solid #dfdfe4;
  border-radius: 12px;
  padding: 12px 14px;
  font-size: 14px;
  color: #222;
  display: flex;
  align-items: center;
  gap: 8px;
}

.workflow-ping-instructions-modal .actions {
  margin-top: 18px;
}

.workflow-ping-instructions-modal .open-btn {
  width: 100%;
}

@media (max-width: 767px) {
  .workflow-ping-instructions-modal .modal-content-wrapper {
    width: 100%;
    max-width: 100%;
    max-height: calc(100vh - 10px);
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .workflow-ping-instructions-modal .modal-content-wrapper::-webkit-scrollbar {
    width: 0;
    height: 0;
    display: none;
  }

  .workflow-ping-instructions-modal h3 {
    font-size: 28px;
  }

  .workflow-ping-instructions-modal .subtitle {
    font-size: 14px;
    margin: 10px 0 16px;
  }

  .workflow-ping-instructions-modal .step-content p {
    font-size: 13px;
  }

  .workflow-ping-instructions-modal .step {
    padding: 12px;
    grid-template-columns: 34px minmax(0, 1fr);
    gap: 10px;
  }
}
</style>
